import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useContacts } from '../contexts/ContactsContext';
import { useInvoices } from '../contexts/InvoicesContext';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import { personalFlag, isViewAvailable } from '../nav/views';
import { userMeetsPlan } from './PlanGate';
import { Button, DashboardCard, HeaderAction } from '../ui';
import './dashboard.css';

const KEY = 'aidey_onboarding';

// Exported for navTargets.test.js: every step's `nav` must be a real view.
export const STEPS = [
  {
    id: 'setup_profile',
    title: 'Set up your profile and preferences',
    desc: 'Add your name, timezone, and connect your calendar.',
    nav: 'Settings',
    navLabel: 'Go to Settings →',
    check: () => {
      try {
        const p = JSON.parse(localStorage.getItem('userProfile') || '{}');
        return !!(p.name || p.displayName);
      } catch { return false; }
    },
  },
  {
    id: 'add_task',
    title: 'Add your first task',
    desc: 'Create a task and let Aidey help you stay on top of your day.',
    nav: 'Tasks',
    navLabel: 'Go to Tasks →',
    check: (contacts, invoices, tasks) => tasks && tasks.length > 0,
  },
  {
    id: 'chat_aidey',
    title: 'Chat with Aidey',
    desc: 'Ask "What\'s on my agenda today?" or "Book a meeting tomorrow at 2pm".',
    nav: 'AideyChat',
    navLabel: 'Open Aidey →',
    check: () => {
      try { return JSON.parse(localStorage.getItem('aidey_sessions') || '[]').length > 0; } catch { return false; }
    },
  },
  {
    id: 'add_contact',
    title: 'Add a contact',
    desc: 'Add a client or colleague to your contacts.',
    nav: 'Contacts',
    navLabel: 'Go to Contacts →',
    check: (contacts) => contacts.length > 0,
  },
  {
    id: 'create_invoice',
    title: 'Create your first invoice',
    desc: 'Create a draft invoice and send it to a client.',
    nav: 'Invoices',
    navLabel: 'Go to Invoicing →',
    check: (contacts, invoices) => invoices.length > 0,
  },
  {
    id: 'setup_scheduling',
    title: 'Set up your scheduling page',
    desc: 'Create your public booking page and share your link with clients.',
    // 'Scheduler' is the real scheduler. 'Scheduling' was the Block 251 localStorage
    // prototype, which never saved anything, and this step used to read the flag only
    // that prototype set, so it could only ever be ticked by hand. It now ticks itself
    // when the real Scheduler has at least one ACTIVE meeting type (GET /api/meetings/types).
    nav: 'Scheduler',
    navLabel: 'Go to Scheduler →',
    check: (contacts, invoices, tasks, extra = {}) => extra.hasActiveMeetingType === true,
  },
];

function loadProgress() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { return {}; }
}
function saveProgress(p) {
  localStorage.setItem(KEY, JSON.stringify(p));
}

export default function OnboardingChecklist({ onNavigate, tasks = [] }) {
  const { t } = useTranslation('common');
  const { contacts } = useContacts();
  const { invoices } = useInvoices();
  const { currentUser, workspaces, activeWorkspaceId } = useCurrentUser();
  // Only the steps whose page exists in this workspace: a Personal workspace has no
  // Invoicing or Scheduler, and a step must never open a page that is not there.
  const isPersonal = personalFlag(workspaces, activeWorkspaceId);
  const steps = useMemo(() => STEPS.filter(s => isViewAvailable(s.nav, isPersonal)), [isPersonal]);
  const [progress, setProgress] = useState(loadProgress);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || '{}').dismissed || false; } catch { return false; }
  });

  // Sync dismissed state from server preferences (survives cache clears / device switches)
  useEffect(() => {
    if (currentUser?.preferences?.onboardingDismissed && !dismissed) {
      setDismissed(true);
    }
  }, [currentUser?.preferences?.onboardingDismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Does the real Scheduler have an active meeting type? null until known. Asked only
  // where the answer can matter: a Pro plan (the endpoint is Pro-only, so a free plan
  // would get a 403 and an error toast on every dashboard visit), a Business workspace
  // (the step is not shown in Personal), the checklist still showing, and the step not
  // yet done. A failed request leaves it null, so the step simply stays unticked; it
  // never ticks on a guess.
  const isPro = userMeetsPlan(currentUser, 'pro');
  const schedulingDone = !!progress.setup_scheduling;
  const [hasActiveMeetingType, setHasActiveMeetingType] = useState(null);
  useEffect(() => {
    if (!isPro || isPersonal !== false || dismissed || schedulingDone) return;
    let cancelled = false;
    api('/api/meetings/types')
      .then(r => r.json())
      .then(d => { if (!cancelled) setHasActiveMeetingType(Array.isArray(d.types) && d.types.some(t => t.isActive)); })
      .catch(err => { console.error('[onboarding] meeting types check failed:', err); });
    return () => { cancelled = true; };
  }, [isPro, isPersonal, dismissed, schedulingDone]);

  const recheck = useCallback(() => {
    const p = loadProgress();
    const updated = { ...p };
    const extra = { hasActiveMeetingType };
    for (const step of STEPS) {
      if (!updated[step.id] && step.check(contacts, invoices, tasks, extra)) updated[step.id] = true;
    }
    saveProgress(updated);
    setProgress(updated);
  }, [contacts, invoices, tasks, hasActiveMeetingType]);

  // Auto-check when contacts, invoices, or tasks change
  useEffect(() => { recheck(); }, [recheck]);

  // Real-time: re-check when any write site dispatches the progress event
  useEffect(() => {
    window.addEventListener('aidey:onboardingProgress', recheck);
    return () => window.removeEventListener('aidey:onboardingProgress', recheck);
  }, [recheck]);

  // Safety net: re-check on tab focus for multi-tab writes
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [recheck]);

  // Auto-dismiss 2 seconds after all steps are completed
  const completedCount = steps.filter(s => progress[s.id]).length;
  const shownAllDone = completedCount === steps.length;
  // Auto-dismiss still needs EVERY step, not just the ones shown here: the dismissal is
  // saved on the account (preferences.onboardingDismissed), so finishing the Personal
  // subset must not hide a Business checklist whose invoice step is still undone.
  const allDone = STEPS.every(s => progress[s.id]);

  function persistDismiss() {
    const updated = { ...loadProgress(), dismissed: true };
    saveProgress(updated);
    setDismissed(true);
    api('/api/users/me', {
      method: 'PUT',
      body: JSON.stringify({ preferences: { onboardingDismissed: true } }),
    }).catch(() => {});
  }

  useEffect(() => {
    if (allDone && !dismissed) {
      const t = setTimeout(persistDismiss, 2500);
      return () => clearTimeout(t);
    }
  }, [allDone, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  function markDone(stepId) {
    const updated = { ...progress, [stepId]: true };
    saveProgress(updated);
    setProgress(updated);
  }

  function dismiss() {
    persistDismiss();
  }

  if (dismissed) return null;

  const pct = Math.round((completedCount / steps.length) * 100);

  const R = 16;
  const C = 2 * Math.PI * R;
  const remaining = allDone
    ? t('onboarding.closing')
    : shownAllDone
      ? null
      : steps.length - completedCount === 1
        ? t('onboarding.steps_remaining_one')
        : t('onboarding.steps_remaining_other', { count: steps.length - completedCount });

  return (
    // Decision 87: the shared tile. The title IS the fold button (the whole title folds the list);
    // the steps left and Dismiss are on the strip's right; the strip turns mint when all are done.
    <DashboardCard tint={shownAllDone ? 'pastel-mint' : 'pastel-yellow'} className={`dash-onb${collapsed ? ' dash-onb--closed' : ''}`}
      aria-label={t('onboarding.get_started')}
      title={(
        <button type="button" className="dash-onb__toggle" onClick={() => setCollapsed(c => !c)} aria-expanded={!collapsed}>
          <span className="dash-onb__ring" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 40 40">
              <circle className="dash-onb__ring-track" cx="20" cy="20" r={R} fill="none" strokeWidth="4" />
              <circle
                className="dash-onb__ring-fill"
                cx="20" cy="20" r={R} fill="none" strokeWidth="4"
                strokeDasharray={`${C}`}
                strokeDashoffset={`${C * (1 - pct / 100)}`}
                strokeLinecap="round"
              />
            </svg>
            <span className="dash-onb__ring-text">{completedCount}/{steps.length}</span>
          </span>
          <span className="dash-onb__title">{shownAllDone ? t('onboarding.all_done_title') : t('onboarding.get_started')}</span>
          <span className="dash-onb__chev" aria-hidden="true">{collapsed ? '▾' : '▴'}</span>
        </button>
      )}
      meta={(
        <>
          {remaining && <span className="dash-onb__sub">{remaining}</span>}
          <HeaderAction onClick={dismiss}>{t('onboarding.dismiss')}</HeaderAction>
        </>
      )}>
      {/* Steps */}
      {!collapsed && (
        <ul className="dash-onb__steps">
          {steps.map((step) => {
            const done = !!progress[step.id];
            return (
              <li key={step.id} className={`dash-onb__step${done ? ' dash-onb__step--done' : ''}`}>
                {/* Checkbox */}
                <button
                  type="button"
                  className="dash-onb__box ui-hit"
                  onClick={() => !done && markDone(step.id)}
                  title={done ? 'Completed' : 'Mark as done'}
                  aria-pressed={done}
                  aria-label={done ? `${step.title}: done` : `Mark "${step.title}" as done`}
                >
                  {done && <span aria-hidden="true">✓</span>}
                </button>

                <div className="dash-onb__main">
                  <button
                    type="button"
                    className="dash-onb__name"
                    onClick={() => !done && onNavigate && onNavigate(step.nav)}
                    disabled={done || !onNavigate}
                  >
                    {step.title}
                  </button>
                  <span className="dash-onb__desc">{step.desc}</span>
                  {!done && onNavigate && (
                    <Button className="dash-onb__go" variant="tertiary" size="sm" onClick={() => onNavigate(step.nav)}>
                      {step.navLabel}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </DashboardCard>
  );
}
